import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import {
  CloseOutlined,
  MinusOutlined,
  PlusOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { ReactComponent as CloseIconSvg } from "@/src/assets/icons/icon-cancle-black.svg";
import { ReactComponent as AddIconSvg } from "@/src/assets/icons/icon-add-black.svg";
import { ReactComponent as MinusIconSvg } from "@/src/assets/icons/icon-minus.svg";
import { ReactComponent as ArrowHookRedIcon } from "@/src/assets/icons/icon-arrow-hook-red.svg";
import { ReactComponent as CheckmarkIcon } from "@/src/assets/icons/icon-checkmark.svg";
import { ReactComponent as ArrowRight } from "@/src/assets/icons/arrow-right-shape.svg";
import useAlert from "@/src/hooks/useAlert";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { RejectReasonModal } from "../RejectReasonModal";
import { Button } from "@/src/components/atom/Button";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface SignatureFullscreenViewerProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl: string;
  contractNo: string;
  userType: "EXPORTER" | "IMPORTER";
  onSign: () => void;
  onReject: (reason?: string) => void;
  currentStep?: string;
  nextStep?: string;
}

const FullscreenContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  z-index: 1000;
`;

const Toolbar = styled.div`
  background: #4a5256;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PageInfo = styled.div`
  color: white;
  font-size: 15px;
  font-family: "Pretendard";
  font-weight: 500;
  line-height: 20px;
`;

const ToolbarCenter = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const ZoomPercentage = styled.div`
  height: 32px;
  padding: 0 12px;
  background: #6e777c;
  box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 15px;
  font-family: "Pretendard";
  font-weight: 500;
  line-height: 20px;
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  padding: 6px;
  background: white;
  box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f5f5f5;
  }

  svg {
    width: 20px;
    height: 20px;
    color: #222729;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  padding: 24px;
  background: #5e696e;
  position: relative;
`;

const PDFContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 900px;
  margin: 0 auto;

  /* react-pdf 스타일 조정 */
  .react-pdf__Page {
    margin: 0 auto;
    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
  }

  .react-pdf__Page__textContent {
    user-select: text;
  }

  .react-pdf__Page__annotations {
    user-select: none;
  }
`;

const ActionBar = styled.div`
  background: white;
  box-shadow: 0px -4px 16px rgba(0, 0, 0, 0.12);
  padding: 16px 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
`;

const ActionBarText = styled.span`
  text-align: center;
  color: #222729;
  font-size: 16px;
  font-family: "Pretendard";
  font-weight: 500;
  line-height: 24px;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const RejectButton = styled(Button)`
  width: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;

  &:focus {
    outline: none;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SignButton = styled(Button)`
  width: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PageNavigation = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  z-index: 100;
`;

const PageButton = styled.button`
  width: 40px;
  height: 40px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.2);
  border: none;
  border-radius: 999px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.4);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: white !important;

    path {
      fill: white !important;
    }
  }
`;

export const SignatureFullscreenViewer: React.FC<
  SignatureFullscreenViewerProps
> = ({
  isOpen,
  onClose,
  pdfUrl,
  contractNo,
  userType,
  onSign,
  onReject,
  currentStep,
  nextStep,
}) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isSignLoading, setIsSignLoading] = useState(false);
  const [signCompleted, setSignCompleted] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  // 컨테이너 너비 계산
  React.useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth - 48); // padding 고려
      }
    };

    updateContainerWidth();
    window.addEventListener("resize", updateContainerWidth);
    return () => window.removeEventListener("resize", updateContainerWidth);
  }, []);

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.1, 2.0));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.1, 0.5));
  };

  const handleSign = async () => {
    setIsSignLoading(true);
    try {
      await onSign();
    } catch (error) {
      console.error("Sign error:", error);
    } finally {
      setIsSignLoading(false);
    }
  };

  const currentPdfUrl = pdfUrl;

  if (!isOpen) return null;

  return (
    <FullscreenContainer>
      <Toolbar>
        <PageInfo>
          Total {pageNumber}/{numPages || "?"}
        </PageInfo>
        <ToolbarCenter>
          <IconButton onClick={handleZoomOut}>
            <MinusIconSvg />
          </IconButton>
          <ZoomPercentage>{Math.round(scale * 100)}%</ZoomPercentage>
          <IconButton onClick={handleZoomIn}>
            <AddIconSvg />
          </IconButton>
        </ToolbarCenter>
        <IconButton onClick={onClose}>
          <CloseIconSvg />
        </IconButton>
      </Toolbar>

      <ContentArea ref={containerRef}>
        <PDFContainer>
          <Document
            file={currentPdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<div>{t("common:loading")}</div>}
            error={<div>{t("common:error.loadFailed")}</div>}
          >
            <Page
              pageNumber={pageNumber}
              width={
                containerWidth > 0 ? Math.min(containerWidth, 800) : undefined
              }
              scale={scale}
              renderTextLayer={true}
              renderAnnotationLayer={true}
            />
          </Document>
        </PDFContainer>

        {/* 페이지 네비게이션 - ContentArea 중앙에 배치 */}
        {numPages && numPages > 1 && (
          <PageNavigation>
            <PageButton
              onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
              disabled={pageNumber <= 1}
            >
              <ArrowRight
                style={{
                  transform: "rotate(180deg)",
                  width: 16,
                  height: 16,
                  fill: "#FFFFFF",
                }}
              />
            </PageButton>
            <PageButton
              onClick={() =>
                setPageNumber((prev) => Math.min(prev + 1, numPages))
              }
              disabled={pageNumber >= numPages}
            >
              <ArrowRight style={{ width: 16, height: 16, fill: "#FFFFFF" }} />
            </PageButton>
          </PageNavigation>
        )}
      </ContentArea>

      <ActionBar>
        <ActionBarText>{t("contract:signature.confirmAndSign")}</ActionBarText>
        <ActionButtons>
          <RejectButton
            buttonColor="red"
            buttonGrade="secondary"
            buttonSize={40}
            onClick={() => setShowRejectModal(true)}
            disabled={isSignLoading || signCompleted}
          >
            <ArrowHookRedIcon />
            {t("contract:signature.reject")}
          </RejectButton>
          <SignButton
            buttonColor="blue"
            buttonGrade="primary"
            buttonSize={40}
            onClick={handleSign}
            disabled={isSignLoading || signCompleted}
            isLoading={isSignLoading}
          >
            <CheckmarkIcon />
            {isSignLoading ? t("common:loading") : t("contract:signature.sign")}
          </SignButton>
        </ActionButtons>
      </ActionBar>

      {/* 반려 사유 입력 모달 */}
      <RejectReasonModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onSubmit={async (reason) => {
          try {
            await onReject(reason);
            setTimeout(() => {
              onClose();
            }, 1000);
          } catch (error) {
            console.error("Reject error:", error);
          }
        }}
      />
    </FullscreenContainer>
  );
};
